/**
 * Hook pour gérer le cache des données employés et cabinet
 * Évite les rechargements répétés inutiles
 */

import { useEffect, useState, useRef } from 'react';
import { cacheGet, cacheSet, emitCacheUpdated } from '../utils/cache';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

/**
 * Hook pour charger et cacher les données cabinet
 */
export function useCabinetCache() {
  const [state, setState] = useState(() => ({
    cabinet: cacheGet('salary/cabinet', null),
    loading: false,
    error: null
  }));

  const fetchedRef = useRef(false);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    
    // Si déjà en cache et déjà fetché, skip
    if (state.cabinet && fetchedRef.current) return;
    
    // Si pas encore fetché, charger
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      
      const loadCabinet = async () => {
        // 🔥 FIX: Annuler toute requête précédente
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        
        if (isMountedRef.current) {
          setState(s => ({ ...s, loading: true, error: null }));
        }
        
        try {
          const response = await fetch(API_ENDPOINTS.CABINETS_LIST(), {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include',
            signal
          });

          // 🔥 FIX: Vérifier si la requête a été annulée
          if (signal.aborted || !isMountedRef.current) {
            return;
          }

          if (!response.ok) {
            if (response.status === 404) {
              cacheSet('salary/cabinet', null);
              if (isMountedRef.current) {
                setState({ cabinet: null, loading: false, error: null });
              }
              return;
            }
            throw new Error(`Erreur HTTP: ${response.status}`);
          }

          const data = await response.json();
          
          // 🔥 FIX: Vérifier à nouveau après l'async
          if (signal.aborted || !isMountedRef.current) {
            return;
          }
          
          const cabinet = (data && Array.isArray(data.data) && data.data.length > 0) 
            ? data.data[0] 
            : null;
          
          cacheSet('salary/cabinet', cabinet);
          emitCacheUpdated('salary/cabinet');
          
          // 🔥 FIX: Ne mettre à jour que si le composant est encore monté
          if (isMountedRef.current) {
            setState({ cabinet, loading: false, error: null });
          }
        } catch (err) {
          // 🔥 FIX: Ignorer les erreurs d'abort
          if (err.name === 'AbortError' || signal.aborted) {
            return;
          }
          
          console.error('Erreur chargement cabinet:', err);
          if (isMountedRef.current) {
            setState(s => ({ ...s, loading: false, error: err.message }));
          }
        }
      };

      loadCabinet();
    }
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Écouter les mises à jour du cache
  const isMountedListenerRef = useRef(true);
  
  useEffect(() => {
    isMountedListenerRef.current = true;
    
    const onUpd = (e) => {
      if (e?.detail?.topic === 'salary/cabinet' && isMountedListenerRef.current) {
        const cached = cacheGet('salary/cabinet', null);
        if (isMountedListenerRef.current) {
          setState(s => ({ ...s, cabinet: cached }));
        }
      }
    };

    window.addEventListener('cc:cache-updated', onUpd);
    return () => {
      isMountedListenerRef.current = false;
      window.removeEventListener('cc:cache-updated', onUpd);
    };
  }, []);

  const refresh = () => {
    fetchedRef.current = false;
    setState(s => ({ ...s, loading: true }));
  };

  return { ...state, refresh };
}

/**
 * Hook pour charger et cacher les données employés par opérateur
 */
export function useEmployeesCache(operatorIds = []) {
  const [state, setState] = useState(() => {
    // Charger depuis le cache
    const cached = cacheGet('salary/employees', {});
    return {
      employees: cached,
      loading: false,
      error: null
    };
  });

  const fetchedIdsRef = useRef(new Set());
  const isLoadingRef = useRef(false);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (!operatorIds || operatorIds.length === 0) return;
    if (isLoadingRef.current) return;

    // Trouver les IDs à charger (pas encore fetchés)
    const idsToFetch = operatorIds.filter(id => !fetchedIdsRef.current.has(id));
    
    if (idsToFetch.length === 0) return;

    const loadEmployees = async () => {
      // 🔥 FIX: Annuler toute requête précédente
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      isLoadingRef.current = true;
      if (isMountedRef.current) {
        setState(s => ({ ...s, loading: true }));
      }

      try {
        const currentCache = cacheGet('salary/employees', {});
        const newCache = { ...currentCache };

        // Charger les données pour chaque nouvel opérateur
        await Promise.all(idsToFetch.map(async (operatorId) => {
          // Vérifier si la requête a été annulée
          if (signal.aborted || !isMountedRef.current) {
            return;
          }
          
          try {
            const response = await fetch(API_ENDPOINTS.EMPLOYEES_LIST(operatorId), {
              method: 'GET',
              headers: getAuthHeaders(),
              credentials: 'include',
              signal
            });

            // Vérifier à nouveau après l'async
            if (signal.aborted || !isMountedRef.current) {
              return;
            }

            if (response.ok) {
              const data = await response.json();
              const hasData = data.data && data.data.length > 0;
              const firstEmployee = hasData ? data.data[0] : null;
              
              newCache[operatorId] = {
                hasData,
                count: data.data ? data.data.length : 0,
                employeeId: firstEmployee?.id || null,
                employee: firstEmployee,
                cachedAt: Date.now()
              };
            } else {
              newCache[operatorId] = { 
                hasData: false, 
                count: 0, 
                employeeId: null, 
                employee: null,
                cachedAt: Date.now()
              };
            }
            
            // Marquer comme fetché
            fetchedIdsRef.current.add(operatorId);
          } catch (err) {
            if (err.name !== 'AbortError') {
              console.error(`Erreur chargement employé ${operatorId}:`, err);
            }
          }
        }));

        // 🔥 FIX: Vérifier que le composant est encore monté avant de mettre à jour
        if (signal.aborted || !isMountedRef.current) {
          isLoadingRef.current = false;
          return;
        }

        // Mettre à jour le cache
        cacheSet('salary/employees', newCache);
        emitCacheUpdated('salary/employees');
        
        if (isMountedRef.current) {
          setState({ employees: newCache, loading: false, error: null });
        }
      } catch (err) {
        // 🔥 FIX: Ignorer les erreurs d'abort
        if (err.name === 'AbortError' || signal.aborted) {
          isLoadingRef.current = false;
          return;
        }
        
        console.error('Erreur chargement employés:', err);
        if (isMountedRef.current) {
          setState(s => ({ ...s, loading: false, error: err.message }));
        }
      } finally {
        isLoadingRef.current = false;
      }
    };

    loadEmployees();
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [operatorIds]);

  // Écouter les mises à jour du cache
  const isMountedListenerRef = useRef(true);
  
  useEffect(() => {
    isMountedListenerRef.current = true;
    
    const onUpd = (e) => {
      if (e?.detail?.topic === 'salary/employees' && isMountedListenerRef.current) {
        const cached = cacheGet('salary/employees', {});
        if (isMountedListenerRef.current) {
          setState(s => ({ ...s, employees: cached }));
        }
      }
    };

    window.addEventListener('cc:cache-updated', onUpd);
    return () => {
      isMountedListenerRef.current = false;
      window.removeEventListener('cc:cache-updated', onUpd);
    };
  }, []);

  const refresh = () => {
    fetchedIdsRef.current.clear();
    isLoadingRef.current = false;
    setState(s => ({ ...s, loading: true }));
  };

  return { ...state, refresh };
}

/**
 * Hook pour vérifier si une fiche de paie existe (avec cache)
 */
export function usePayrollCache(operatorId, currentMonth) {
  const [state, setState] = useState(() => {
    if (!operatorId || !currentMonth) {
      return { loading: false, exists: false };
    }

    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const cacheKey = `payroll/${operatorId}/${year}-${month}`;
    
    return {
      loading: false,
      exists: cacheGet(cacheKey, false)
    };
  });

  const fetchedRef = useRef(false);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (!operatorId || !currentMonth || fetchedRef.current) return;

    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const periodMonth = `${year}-${month}`;
    const cacheKey = `payroll/${operatorId}/${periodMonth}`;

    const checkPayroll = async () => {
      // 🔥 FIX: Annuler toute requête précédente
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      if (isMountedRef.current) {
        setState(s => ({ ...s, loading: true }));
      }
      
      try {
        const url = `${API_ENDPOINTS.PAYROLL_LIST()}?operator_id=${operatorId}&period_month=${periodMonth}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders(),
          credentials: 'include',
          signal
        });

        // 🔥 FIX: Vérifier si la requête a été annulée
        if (signal.aborted || !isMountedRef.current) {
          return;
        }

        if (response.ok) {
          const data = await response.json();
          const exists = data.items && data.items.length > 0;
          
          cacheSet(cacheKey, exists);
          emitCacheUpdated(cacheKey);
          
          // 🔥 FIX: Ne mettre à jour que si le composant est encore monté
          if (isMountedRef.current) {
            setState({ loading: false, exists });
          }
        } else {
          cacheSet(cacheKey, false);
          if (isMountedRef.current) {
            setState({ loading: false, exists: false });
          }
        }
      } catch (err) {
        // 🔥 FIX: Ignorer les erreurs d'abort
        if (err.name === 'AbortError' || signal.aborted) {
          return;
        }
        
        console.error('Erreur vérification payroll:', err);
        if (isMountedRef.current) {
          setState({ loading: false, exists: false });
        }
      } finally {
        fetchedRef.current = true;
      }
    };

    checkPayroll();
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [operatorId, currentMonth]);

  return state;
}
